import { NurseLayout } from "@/components/layout/nurse-layout";
import { Link } from "wouter";
import { ArrowLeft, Users, CheckCircle, ExternalLink, TrendingUp } from "lucide-react";

const LEADERSHIP_ROLES = [
  {
    title: "Lead Clinical Nurse Specialist",
    band: "Band 7–8a",
    colour: "border-purple-300",
    bg: "bg-purple-50",
    text: "text-purple-800",
    desc: "Leads the rheumatology CNS team — line management, caseload oversight, service development, and acting as clinical lead for nursing across the department. Often the first formal leadership role for senior CNSs.",
    skills: ["Team management and appraisals", "Service development and QI", "Budget oversight contribution", "External stakeholder liaison (BSR, NHSE)"],
  },
  {
    title: "Matron / Head of Nursing",
    band: "Band 8a–8b",
    colour: "border-indigo-300",
    bg: "bg-indigo-50",
    text: "text-indigo-800",
    desc: "Provides operational leadership across a clinical division. Responsible for nursing standards, staffing, CQC compliance, workforce planning, and patient experience metrics.",
    skills: ["Workforce planning and recruitment", "CQC compliance and governance", "Divisional budget management", "Professional lead for nursing staff"],
  },
  {
    title: "Associate Director of Nursing (ADoN)",
    band: "Band 8c–8d",
    colour: "border-rose-300",
    bg: "bg-rose-50",
    text: "text-rose-800",
    desc: "Strategic nursing leadership at directorate or divisional level. Sits on trust leadership forums, contributes to board-level nursing strategy, and represents nursing professionally across the organisation.",
    skills: ["Board-level engagement", "Strategic workforce and quality planning", "System-wide nursing leadership", "Partnership with ICB and NHSE"],
  },
];

const PROGRAMMES = [
  { name: "NHS Graduate Management Training Scheme (GMTS)", url: "https://www.nhsgraduates.co.uk/", desc: "Competitive two-year rotational programme with NHS — includes health policy, finance, general management, and clinical tracks." },
  { name: "Florence Nightingale Foundation Leadership Programme", url: "https://florence-nightingale-foundation.org.uk/", desc: "Highly regarded leadership development for nurses and midwives, including international leadership programmes and national scholarships." },
  { name: "FMLM (Faculty of Medical Leadership & Management)", url: "https://www.fmlm.ac.uk/", desc: "Multidisciplinary medical and nursing leadership development, including the Clinical Leadership Programme and Fellowship." },
  { name: "NHS Leadership Academy — Edward Jenner / Mary Seacole", url: "https://www.leadershipacademy.nhs.uk/", desc: "Free NHS online leadership programmes: Edward Jenner (foundation), Mary Seacole (first leadership role), Nye Bevan (Board-level) — all free for NHS staff." },
  { name: "King's Fund Leadership Programmes", url: "https://www.kingsfund.org.uk/", desc: "The King's Fund offers senior leadership programmes including Leading in Complexity and Board development for senior nurse leaders." },
  { name: "NHS Aspiring Chief Nurse Programme", url: "https://www.england.nhs.uk/", desc: "NHSE programme preparing senior nurses for Chief Nursing Officer and Director of Nursing roles at trust and system level." },
];

const LEADERSHIP_TIPS = [
  "Start building your leadership portfolio at Band 7 — document QI projects, team management experience, and any budget/governance contributions.",
  "Seek a mentor outside your immediate team — ideally someone operating at the level you aspire to within 3–5 years.",
  "Volunteer for trust-wide groups: nursing and midwifery council, CQC preparation groups, patient experience committees.",
  "Get comfortable with data — NHS leadership increasingly requires comfort with workforce metrics, patient outcome data, and financial literacy.",
  "Apply for at least one external leadership programme (Florence Nightingale Foundation, NHS Leadership Academy) before a senior Band 8 appointment.",
  "Develop a visible professional identity: contribute to BSR/RCN events, write for nursing journals, engage with X/LinkedIn professionally.",
];

export default function NurseLeadership() {
  return (
    <NurseLayout title="Nursing Leadership">
      <Link href="/nurse/career">
        <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Career Hub
        </button>
      </Link>

      <div className="space-y-8">

        <div className="bg-gradient-to-br from-purple-900 to-purple-950 rounded-2xl p-8 text-white">
          <div className="flex items-center gap-4 mb-3">
            <div className="bg-purple-800/60 p-3 rounded-xl">
              <Users className="h-7 w-7 text-purple-300" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Nursing Leadership Pathway</h2>
              <p className="text-purple-300 text-sm">Lead Nurse · Matron · Head of Nursing · ADoN</p>
            </div>
          </div>
          <p className="text-purple-200 text-sm leading-relaxed max-w-3xl">
            Leadership in nursing is increasingly essential alongside clinical expertise. Senior CNSs, ANPs, and Consultant Nurses are expected to demonstrate strategic influence, team leadership, and service development — whether or not they move into formal management roles.
          </p>
        </div>

        {/* Leadership roles */}
        <div className="space-y-4">
          {LEADERSHIP_ROLES.map((role) => (
            <div key={role.title} className={`bg-white rounded-2xl border-2 ${role.colour} p-6 shadow-sm`}>
              <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-lg text-slate-900">{role.title}</h3>
                    <span className={`text-xs px-2.5 py-0.5 rounded-full border font-semibold ${role.bg} ${role.text} border-current/20`}>{role.band}</span>
                  </div>
                </div>
                <TrendingUp className={`h-5 w-5 shrink-0 ${role.text}`} />
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">{role.desc}</p>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Key Skills Required</p>
                <div className="grid sm:grid-cols-2 gap-1.5">
                  {role.skills.map((skill, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className={`h-4 w-4 shrink-0 ${role.text}`} />
                      {skill}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Development programmes */}
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-4">Leadership Development Programmes</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {PROGRAMMES.map((prog) => (
              <a key={prog.name} href={prog.url} target="_blank" rel="noopener noreferrer"
                className="bg-white rounded-xl border border-slate-200 p-4 hover:border-purple-300 hover:shadow-sm transition-all group block">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-semibold text-sm text-slate-900 group-hover:text-purple-700 transition-colors">{prog.name}</p>
                  <ExternalLink className="h-3.5 w-3.5 text-slate-300 group-hover:text-purple-500 shrink-0 mt-0.5 transition-colors" />
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">{prog.desc}</p>
              </a>
            ))}
          </div>
        </div>

        {/* Practical tips */}
        <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6">
          <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-700" />
            Practical Leadership Tips
          </h3>
          <ul className="space-y-2">
            {LEADERSHIP_TIPS.map((tip, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-purple-800">
                <span className="h-5 w-5 rounded-full bg-purple-200 text-purple-800 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </NurseLayout>
  );
}
